import { getStackingContext, getZIndexElement } from '@cfl/ixbrl-tag-renderer';

export class Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;

  constructor(left: number, top: number, width: number, height: number) {
    this.left = left;
    this.top = top;
    this.width = width;
    this.height = height;
    this.right = left + width;
    this.bottom = top + height;
  }
}

export function documentFrameWindow(): Window {
  const documentFrame = <HTMLIFrameElement | null>document.getElementById('documentFrame');
  const documentFrameWindow = documentFrame?.contentWindow;
  if (!documentFrameWindow) {
    throw new Error("Can't find window");
  }
  return documentFrameWindow;
}

export function documentFrameDocument(): Document {
  const documentFrame = <HTMLIFrameElement | null>document.getElementById('documentFrame');
  const documentFrameDocument = documentFrame?.contentDocument || documentFrame?.contentWindow?.document;
  if (!documentFrameDocument) {
    throw new Error("Can't find document");
  }
  return documentFrameDocument;
}

export function documentPositionComparator(a: HTMLElement, b: HTMLElement): number {
  const comparator = a.compareDocumentPosition(b);

  if (comparator & Node.DOCUMENT_POSITION_FOLLOWING) {
    return -1;
  }
  if (comparator & Node.DOCUMENT_POSITION_PRECEDING) {
    return 1;
  }

  return 0;
}

export function getDomOffsetToContainer(elementId: string): number {
  const element = documentFrameDocument().getElementById(elementId);
  let currentOffset = 0;
  let currentNode = getPreviousNode(element);
  while (currentNode) {
    currentOffset += currentNode?.textContent?.length ?? 0;
    currentNode = getPreviousNode(currentNode);
  }
  return currentOffset;
}

export function getDomOffsetToSelection(selection: SelectionDto): { domStartOffset: number; domEndOffset: number } {
  const domOffsetToContainer = getDomOffsetToContainer(selection.containerId);
  return {
    domStartOffset: domOffsetToContainer + selection.startIndex,
    domEndOffset: domOffsetToContainer + selection.endIndex,
  };
}

function getPreviousNode(node: Node | null): Node | undefined {
  let currentNode: Node | null | undefined = node;
  while (currentNode) {
    // stop at the body tag
    if (currentNode.nodeType === Node.ELEMENT_NODE && (currentNode as Element).tagName === 'BODY') {
      break;
    }

    // checking the type of the node is either text or element to make sure we don't include comments
    if (currentNode.nodeType !== Node.TEXT_NODE && currentNode.nodeType !== Node.ELEMENT_NODE) {
      currentNode = currentNode.parentNode;
      continue;
    }

    // skip elements that are part of the <style> or <script>.
    if (currentNode.nodeType === Node.ELEMENT_NODE && ['STYLE', 'SCRIPT'].includes((currentNode as Element).tagName)) {
      currentNode = currentNode.parentNode;
      continue;
    }

    if (currentNode.previousSibling) {
      return currentNode.previousSibling;
    }

    currentNode = currentNode.parentNode;
  }

  return undefined;
}

export function getClosestCommonAncestor(elements: HTMLElement[]): HTMLElement {
  const htmlElement = documentFrameDocument().getElementsByTagName('html')[0];
  const listsOfAncestors: Set<HTMLElement>[] = [];
  for (const element of elements) {
    const ancestors: Set<HTMLElement> = new Set<HTMLElement>();
    let parent = element.parentElement;
    while (parent && parent != htmlElement) {
      ancestors.add(parent);
      parent = parent.parentElement;
    }
    listsOfAncestors.push(ancestors);
  }
  let currentElement = elements[elements.length - 1]?.parentElement;
  while (currentElement && currentElement != htmlElement) {
    let isCommonAncestor = true;
    for (const ancestors of listsOfAncestors) {
      if (!ancestors.has(currentElement)) {
        isCommonAncestor = false;
      }
    }
    if (isCommonAncestor) {
      return currentElement;
    }
    currentElement = currentElement.parentElement;
  }

  return htmlElement;
}

export function getHighestEquivalentAncestor(element: HTMLElement): HTMLElement {
  let highestEquivalentAncestor = element;
  while (highestEquivalentAncestor.parentElement != null && highestEquivalentAncestor.parentElement.textContent == element.textContent) {
    highestEquivalentAncestor = highestEquivalentAncestor.parentElement;
  }
  return highestEquivalentAncestor;
}

export function isSelectionValid(nodes: ChildNode[]): boolean {
  if (!documentFrameDocument().body.classList.contains('newStyleTagging') || nodes.length < 2) {
    return true;
  }
  const stackingContext = getStackingContext(getZIndexElement(nodes[0]));
  for (const element of nodes) {
    if (getStackingContext(getZIndexElement(element)) != stackingContext) {
      return false;
    }
  }
  return true;
}

export function areElementsEmpty(elements: HTMLElement[]): boolean {
  return !elements.reduce((fullText, element) => fullText + element.textContent, '').trim().length;
}
